/**
 * InvoiceModal.js
 *
 * Admin view of a user's subscription as a payment receipt in DDAG's invoice layout:
 *   - "Invoice Receipt" tab: the receipt; trial and free-access users get a no-charge statement
 *   - "Custom Invoice" tab: edit line items, tax, payment method and reference
 *   - "Print Invoice": print the receipt
 *   - "Download PDF": the issued GST tax invoice for paid users, otherwise the receipt
 */

import React, { useState, useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import * as invoiceService from '../../services/invoiceService';
import { downloadInvoice } from '../../services/subscriptionService';
import { FREE_TRIAL_PLAN_NAME } from '../../helpers/subscription.helpers';
import { GST_STATES } from '../../helpers/gst.helpers';
import './InvoiceModal.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(val) {
  return Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(d) {
  if (!d) return new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Same seller block as the server-side tax invoice (backend/config/gst.js).
const SELLER = {
  legalName: 'DDAG PRIVATE LIMITED',
  address: 'C-1835/1A, Rajajipuram, Lucknow-226017, Uttar Pradesh',
  cin: 'U74200UP2020PTC125879',
  msme: 'UDYAM-UP-50-0053172',
  gstin: '09AAHCD6224B1ZY',
  phone: '+91-9319438989',
  email: 'info@dhawanacademy.com',
  website: 'www.dhawanacademy.com',
};
const SAC_CODE = '997331';

const LOGO_PATHS = { xlog3d: 'invoice/xlog3d-logo.png', ddag: 'invoice/ddag-logo.png' };
const DDAG_LOGO_ASPECT = 440 / 262;
const assetUrl = (path) => new URL(`${process.env.PUBLIC_URL || '.'}/${path}`, document.baseURI).href;

const STATE_NAMES = Object.fromEntries(GST_STATES);
const stateLabel = (code) => (code && STATE_NAMES[code] ? `${STATE_NAMES[code]} (State Code ${code})` : '');

// Pre-GST list prices; actual charges are on the GST invoices above the receipt.
const PLAN_PRICES = { THREE_MONTHS: 999, ANNUAL: 2999 };
const PLAN_NAMES = {
  THREE_MONTHS: 'XLog3D Three Months Plan',
  ANNUAL: 'XLog3D Annual Plan',
};

const STATUS_LABELS = {
  PAID: { short: 'PAID IN FULL', pill: 'PAID & VERIFIED' },
  TRIAL: { short: 'FREE TRIAL - NO CHARGE', pill: 'FREE TRIAL' },
  FREE: { short: 'FREE ACCESS - NO CHARGE', pill: 'FREE ACCESS' },
  PENDING: { short: 'PAYMENT PENDING', pill: 'PENDING' },
};

function statusLabel(status) {
  return STATUS_LABELS[status] || STATUS_LABELS.PAID;
}

/** Loads a same-origin image as a data URL for jsPDF; null when it cannot be read. */
async function imageDataUrl(path) {
  try {
    const res = await fetch(assetUrl(path));
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

// Shared by the in-app view and the print window, so both look the same.
const PAPER_CSS = `
.rcpt { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1a1a2e; font-size: 12.5px; line-height: 1.45; background: #fff; }
.rcpt-head { display: grid; grid-template-columns: 72px 1fr 120px; align-items: start; gap: 12px; }
.rcpt-logo { height: 64px; width: auto; }
.rcpt-logo--right { justify-self: end; }
.rcpt-title { text-align: center; }
.rcpt-title h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: 0.04em; }
.rcpt-title .rcpt-sub { font-size: 10.5px; color: #555b66; margin-top: 2px; }
.rcpt-title .rcpt-seller { font-size: 15px; font-weight: 800; margin-top: 8px; }
.rcpt-title .rcpt-seller-line { font-size: 11px; color: #333; }
.rcpt-rule { border: 0; border-top: 1px solid #c9ced6; margin: 14px 0 12px; }
.rcpt-parties { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.rcpt-label { font-weight: 700; margin-bottom: 4px; }
.rcpt-meta { display: grid; grid-template-columns: auto 1fr; gap: 3px 12px; }
.rcpt-meta dt { font-weight: 700; }
.rcpt-meta dd { margin: 0; word-break: break-word; }
.rcpt-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
.rcpt-table th { background: #eef1f5; text-align: left; font-size: 11px; padding: 6px 8px; }
.rcpt-table td { padding: 8px; vertical-align: top; border-bottom: 1px solid #c9ced6; }
.rcpt-table .num { text-align: right; white-space: nowrap; }
.rcpt-muted { color: #555b66; font-size: 11px; }
.rcpt-totals { margin: 10px 0 0 auto; width: 300px; }
.rcpt-totals div { display: flex; justify-content: space-between; padding: 3px 0; }
.rcpt-totals .rcpt-grand { background: #1a1a2e; color: #fff; font-weight: 700; padding: 6px 8px; margin-top: 4px; }
.rcpt-nocharge { margin-top: 16px; border: 1.5px dashed #b45309; color: #92400e; background: #fffbeb; padding: 10px 12px; font-weight: 700; text-align: center; }
.rcpt-foot { margin-top: 22px; font-size: 11px; color: #555b66; }
.rcpt-sign { text-align: right; font-weight: 700; color: #1a1a2e; margin-top: 26px; }
`;

export default function InvoiceModal({ user, onClose }) {
  const [tab, setTab] = useState('receipt'); // 'receipt' | 'custom'

  const sub = user?.subscription || {};
  const isTrialInitial = String(sub.status || '').toUpperCase().includes('TRIAL');
  // Access granted by an admin or the launch offer has no Razorpay subscription behind it.
  const isFreeInitial = !isTrialInitial && !sub.razorpaySubscriptionId;
  const planCode = String(sub.planCode || '').toUpperCase();

  const defaultPlanName = isTrialInitial
    ? `XLog3D ${FREE_TRIAL_PLAN_NAME} (21 Days)`
    : `${PLAN_NAMES[planCode] || 'XLog3D Subscription'}${isFreeInitial ? ' (complimentary access)' : ''}`;

  const defaultBasePrice = isTrialInitial || isFreeInitial ? 0 : PLAN_PRICES[planCode] || 0;

  // Customizable receipt state. The receipt number is optional: a charged user's
  // statutory number is the GST invoice's, and no-charge users have none.
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentStatus, setPaymentStatus] = useState(isTrialInitial ? 'TRIAL' : isFreeInitial ? 'FREE' : 'PAID');
  const [paymentMode, setPaymentMode] = useState(
    sub.razorpaySubscriptionId ? 'Credit / Debit Card (Razorpay Secure)' : (isTrialInitial ? 'In-App Trial Access' : isFreeInitial ? 'Not applicable (free access)' : 'Credit / Debit Card (Online PG)')
  );
  const [bankOrCard, setBankOrCard] = useState(
    isTrialInitial ? 'No Card Required (Trial)' : isFreeInitial ? 'No payment' : 'Razorpay'
  );
  const [transactionRef, setTransactionRef] = useState(
    sub.razorpaySubscriptionId || sub.razorpayCustomerId || ''
  );

  const [items, setItems] = useState([
    {
      description: defaultPlanName,
      subDescription: 'Single-user subscription linked to the registered email ID, with technical support',
      quantity: 1,
      unitPrice: defaultBasePrice
    }
  ]);
  const [taxPercent, setTaxPercent] = useState(18);
  // Once the admin applies the custom form, the receipt shows those figures
  // instead of the latest issued GST invoice's.
  const [customised, setCustomised] = useState(false);

  const printRef = useRef(null);

  // Statutory invoices raised automatically for this user's Razorpay charges.
  const [gstInvoices, setGstInvoices] = useState([]);
  const [gstError, setGstError] = useState('');
  useEffect(() => {
    let alive = true;
    invoiceService
      .fetchInvoices({ userId: user.id, limit: 50 })
      .then((data) => {
        if (alive) setGstInvoices(((data && data.invoices) || []).filter((inv) => inv.razorpayPaymentId));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user.id]);

  // Computed Totals (custom form)
  const subtotal = items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const taxAmount = (subtotal * (Number(taxPercent) || 0)) / 100;
  const grandTotal = subtotal + taxAmount;

  // ── Receipt model: one source for the screen, print and PDF ──
  const latestGst = gstInvoices[0] || null;
  const charged = paymentStatus === 'PAID';
  const fromIssued = charged && !!latestGst && !customised;

  const buyerName = (latestGst && latestGst.buyerName) || user.name || user.email || '';
  const buyerLines = [
    user.organization && user.organization !== buyerName && user.organization !== 'Individual' ? user.organization : null,
    user.email && user.email !== buyerName ? user.email : null,
  ];
  if (latestGst) {
    const place = [latestGst.buyerAddress, latestGst.buyerCity].filter(Boolean).join(', ');
    if (place) buyerLines.push(`Address: ${place}`);
    if (stateLabel(latestGst.placeOfSupply)) buyerLines.push(`State: ${stateLabel(latestGst.placeOfSupply)}`);
    buyerLines.push(`GSTIN: ${latestGst.buyerGstin || 'Unregistered'}`);
  }

  const period = sub.endsAt
    ? `${fmtDate(sub.createdAt)} - ${fmtDate(sub.endsAt)}`
    : sub.trialEnds
      ? `Until ${fmtDate(sub.trialEnds)}`
      : '';
  const meta = [
    charged && latestGst ? ['Tax Invoice No.', `${latestGst.invoiceNumber} (${fmtDate(latestGst.issueDate)})`] : null,
    invoiceNumber.trim() ? ['Receipt No.', invoiceNumber.trim()] : null,
    ['Receipt Date', fmtDate(invoiceDate)],
    ['Status', statusLabel(paymentStatus).short],
    period ? ['Access Period', period] : null,
    ['SAC Code', SAC_CODE],
    ['Payment', charged ? paymentMode : 'No charge'],
    charged && (bankOrCard || '').trim() ? ['Card / Bank', bankOrCard] : null,
    charged && ((latestGst && latestGst.razorpayPaymentId) || transactionRef)
      ? ['Payment Ref.', (fromIssued && latestGst.razorpayPaymentId) || transactionRef || latestGst.razorpayPaymentId]
      : null,
  ].filter(Boolean);

  const lines = fromIssued
    ? (latestGst.items || []).map((it) => ({
        description: it.description,
        sub: user.email ? `Licensed to ${user.email}` : '',
        quantity: Number(it.quantity) || 1,
        amount: Number(it.amount) || 0,
      }))
    : items.map((it) => ({
        description: it.description,
        sub: it.subDescription || (user.email ? `Licensed to ${user.email}` : ''),
        quantity: Number(it.quantity) || 0,
        amount: (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0),
      }));

  let taxRows = [];
  let total = 0;
  if (charged) {
    if (fromIssued) {
      const pct = Number(latestGst.taxPercent) || 18;
      const intra = Number(latestGst.igstAmount) === 0;
      taxRows = [['Taxable Value', Number(latestGst.subtotal)]];
      if (intra) {
        taxRows.push([`CGST @ ${pct / 2}%`, Number(latestGst.cgstAmount)], [`SGST @ ${pct / 2}%`, Number(latestGst.sgstAmount)]);
      } else {
        taxRows.push([`IGST @ ${pct}%`, Number(latestGst.igstAmount)]);
      }
      if (Number(latestGst.roundOff)) taxRows.push(['Round Off', Number(latestGst.roundOff)]);
      total = Number(latestGst.total);
    } else {
      const pct = Number(taxPercent) || 0;
      // Without an issued invoice the buyer's state is unknown; seller-state split is the default.
      const intra = latestGst ? Number(latestGst.igstAmount) === 0 : true;
      taxRows = [['Taxable Value', subtotal]];
      if (pct) {
        if (intra) taxRows.push([`CGST @ ${pct / 2}%`, taxAmount / 2], [`SGST @ ${pct / 2}%`, taxAmount / 2]);
        else taxRows.push([`IGST @ ${pct}%`, taxAmount]);
      }
      total = grandTotal;
    }
  }

  const noChargeNote =
    paymentStatus === 'PENDING' ? 'Payment pending - no tax invoice issued yet.' : 'No charge - no tax invoice issued.';
  const footNote = charged
    ? 'This is a computer-generated receipt and does not require a signature. The GST tax invoice for each charge is issued separately and emailed to the buyer.'
    : 'This is a computer-generated statement and does not require a signature.';
  const fileBase = (invoiceNumber.trim() || (charged && latestGst ? latestGst.invoiceNumber : `Receipt-${String(user.id || '').slice(0, 8)}`))
    .replace(/\//g, '-');

  // Item Handlers
  const handleAddItem = () => {
    setItems(prev => [
      ...prev,
      {
        description: 'Additional item',
        subDescription: '',
        quantity: 1,
        unitPrice: 999
      }
    ]);
  };

  const handleRemoveItem = (idx) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  // Robust isolated Print function
  const handlePrint = () => {
    const printContent = document.getElementById('printable-invoice-paper');
    if (!printContent) {
      window.print();
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=800');
    if (!printWindow) {
      window.print();
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${fileBase} - Payment Receipt</title>
          <style>body { margin: 24px; } ${PAPER_CSS}</style>
        </head>
        <body>
          <div style="max-width: 800px; margin: 0 auto;">${printContent.innerHTML}</div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // A4 PDF in the same layout as the server's tax invoice. jsPDF's built-in fonts have no ₹ glyph.
  const handlePdfDownload = async () => {
    try {
      const [xlogLogo, ddagLogo] = await Promise.all([imageDataUrl(LOGO_PATHS.xlog3d), imageDataUrl(LOGO_PATHS.ddag)]);
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const L = 17;
      const W = pageWidth - 2 * L;
      const R = L + W;
      const C = pageWidth / 2;
      const money = (v) => `Rs. ${fmt(v)}`;
      const dark = () => doc.setTextColor(26, 26, 46);
      const muted = () => doc.setTextColor(85, 91, 102);

      const LOGO_H = 20;
      if (xlogLogo) doc.addImage(xlogLogo, 'PNG', L, 14, LOGO_H, LOGO_H);
      if (ddagLogo) doc.addImage(ddagLogo, 'PNG', R - LOGO_H * DDAG_LOGO_ASPECT, 14, LOGO_H * DDAG_LOGO_ASPECT, LOGO_H);

      let y = 20;
      dark();
      doc.setFont('helvetica', 'bold').setFontSize(17).text('PAYMENT RECEIPT', C, y, { align: 'center' });
      muted();
      doc.setFont('helvetica', 'normal').setFontSize(8).text(statusLabel(paymentStatus).short, C, y + 5, { align: 'center' });
      dark();
      doc.setFont('helvetica', 'bold').setFontSize(12).text(SELLER.legalName, C, y + 12, { align: 'center' });
      doc.setFont('helvetica', 'normal').setFontSize(8.5);
      doc.text(SELLER.address, C, y + 17, { align: 'center' });
      doc.text(`CIN: ${SELLER.cin}   |   MSME: ${SELLER.msme}   |   GSTIN: ${SELLER.gstin}`, C, y + 21.5, { align: 'center' });
      doc.text(`${SELLER.phone}   |   ${SELLER.email}   |   ${SELLER.website}`, C, y + 26, { align: 'center' });

      y += 31;
      doc.setDrawColor(201, 206, 214).line(L, y, R, y);
      y += 6;

      // Bill To (left) and receipt details (right)
      doc.setFont('helvetica', 'bold').setFontSize(9).text('Bill To', L, y);
      doc.setFont('helvetica', 'normal');
      let by = y + 5;
      [buyerName, ...buyerLines].filter(Boolean).forEach((line) => {
        const wrapped = doc.splitTextToSize(line, 80);
        doc.text(wrapped, L, by);
        by += 4.4 * wrapped.length;
      });

      const mx = L + 88;
      let my = y;
      meta.forEach(([k, v]) => {
        doc.setFont('helvetica', 'bold').text(k, mx, my);
        doc.setFont('helvetica', 'normal');
        const wrapped = doc.splitTextToSize(String(v), R - mx - 30);
        doc.text(wrapped, mx + 30, my);
        my += 4.6 * wrapped.length;
      });
      y = Math.max(by, my) + 4;

      // Items
      doc.setFillColor(238, 241, 245).rect(L, y, W, 7, 'F');
      dark();
      doc.setFont('helvetica', 'bold').setFontSize(8.5);
      doc.text('Description of Service', L + 2, y + 4.8);
      doc.text('SAC', L + 112, y + 4.8);
      doc.text('Qty', L + 134, y + 4.8);
      doc.text('Amount', R - 2, y + 4.8, { align: 'right' });
      y += 12;
      lines.forEach((ln) => {
        doc.setFont('helvetica', 'normal').setFontSize(9);
        dark();
        const desc = doc.splitTextToSize(ln.description || '', 106);
        doc.text(desc, L + 2, y);
        doc.text(SAC_CODE, L + 112, y);
        doc.text(String(ln.quantity), L + 134, y);
        doc.text(money(ln.amount), R - 2, y, { align: 'right' });
        let iy = y + 4.4 * desc.length;
        if (ln.sub) {
          muted();
          doc.setFontSize(7.5);
          const subLines = doc.splitTextToSize(ln.sub, 106);
          doc.text(subLines, L + 2, iy);
          iy += 3.6 * subLines.length;
        }
        y = iy + 2;
        doc.setDrawColor(201, 206, 214).line(L, y, R, y);
        y += 6;
      });

      if (charged) {
        const tx = L + 96;
        doc.setFont('helvetica', 'normal').setFontSize(9);
        dark();
        taxRows.forEach(([k, v]) => {
          doc.text(k, tx, y);
          doc.text(money(v), R - 2, y, { align: 'right' });
          y += 5;
        });
        doc.setFillColor(26, 26, 46).rect(tx - 2, y - 3.5, R - tx + 2, 7.5, 'F');
        doc.setTextColor(255, 255, 255).setFont('helvetica', 'bold');
        doc.text('Total (incl. GST)', tx, y + 1.3);
        doc.text(money(total), R - 2, y + 1.3, { align: 'right' });
        y += 12;
      } else {
        doc.setDrawColor(180, 83, 9).setLineDashPattern([1.5, 1], 0).rect(L, y - 2, W, 10);
        doc.setLineDashPattern([], 0);
        doc.setTextColor(146, 64, 14).setFont('helvetica', 'bold').setFontSize(10);
        doc.text(noChargeNote, C, y + 4.3, { align: 'center' });
        y += 16;
      }

      muted();
      doc.setFont('helvetica', 'normal').setFontSize(8);
      doc.text(doc.splitTextToSize(footNote, W), L, y);
      dark();
      doc.setFont('helvetica', 'bold').text(`For ${SELLER.legalName}`, R, y + 16, { align: 'right' });

      doc.save(`${fileBase}.pdf`);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      // Fallback to print-to-pdf
      handlePrint();
    }
  };

  const handleApplyCustomInvoice = (e) => {
    e.preventDefault();
    setCustomised(true);
    setTab('receipt');
  };


  return (
    <div
      className="inv-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{PAPER_CSS}</style>
      <div className="inv-modal inv-modal--wide">
        {/* ── Modal Top Bar (Navigation) ── */}
        <div className="inv-modal-bar no-print">
          <div className="inv-modal-bar-left">
            <div className="inv-badge-icon">
              <i className="fa fa-file-invoice-dollar" />
            </div>
            <div>
              <div className="inv-modal-title">Payment Receipt</div>
              <div className="inv-modal-subtitle">Subscription and payment summary for this user</div>
            </div>
          </div>

          <div className="inv-header-actions">
            {/* 1. Invoice Receipt Tab Button */}
            <button
              type="button"
              className={`inv-tab-btn ${tab === 'receipt' ? 'inv-tab-btn--active' : ''}`}
              onClick={() => setTab('receipt')}
              title="View the payment receipt"
            >
              <i className="fa fa-receipt" /> Invoice Receipt
            </button>

            {/* 2. Custom Invoice Tab Button */}
            <button
              type="button"
              className={`inv-tab-btn ${tab === 'custom' ? 'inv-tab-btn--active' : ''}`}
              onClick={() => setTab('custom')}
              title="Edit receipt details"
            >
              <i className="fa fa-plus-circle" /> Custom Invoice
            </button>

            {/* 3. Close Button */}
            <button
              type="button"
              className="inv-modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>

        {/* ── Modal Body ── */}
        <div className="inv-modal-body">
          {tab === 'receipt' ? (
            <div className="inv-receipt-container">
              {/* ── Actions Toolbar ── */}
              <div className="inv-toolbar no-print">
                <div className="inv-toolbar-status">
                  <span className={`inv-status-pill ${paymentStatus === 'PAID' ? 'inv-status-pill--paid' : 'inv-status-pill--trial'}`}>
                    <i className="fa fa-check-circle" /> {statusLabel(paymentStatus).pill}
                  </span>
                </div>

                <div className="inv-toolbar-btns">
                  {/* Print Invoice Button */}
                  <button
                    type="button"
                    className="inv-action-btn inv-action-btn--print"
                    onClick={handlePrint}
                    title="Print this receipt"
                  >
                    <i className="fa fa-print" /> Print Invoice
                  </button>

                  {/* Download PDF Button */}
                  <button
                    type="button"
                    className="inv-action-btn inv-action-btn--pdf"
                    onClick={
                      fromIssued
                        ? () => {
                            setGstError('');
                            downloadInvoice(latestGst).catch((e) => setGstError(e.message || 'Download failed'));
                          }
                        : handlePdfDownload
                    }
                    title={fromIssued ? `Download GST tax invoice ${latestGst.invoiceNumber}` : 'Download / Save as PDF'}
                  >
                    <i className="fa fa-download" /> Download PDF
                  </button>
                </div>
              </div>
              {gstError && <div className="inv-download-error no-print">{gstError}</div>}

              {/* ── Printable Receipt, DDAG invoice layout ── */}
              <div className="inv-paper" id="printable-invoice-paper" ref={printRef}>
                <div className="rcpt">
                  <div className="rcpt-head">
                    <img className="rcpt-logo" src={assetUrl(LOGO_PATHS.xlog3d)} alt="XLog3D" />
                    <div className="rcpt-title">
                      <h1>PAYMENT RECEIPT</h1>
                      <div className="rcpt-sub">{statusLabel(paymentStatus).short}</div>
                      <div className="rcpt-seller">{SELLER.legalName}</div>
                      <div className="rcpt-seller-line">{SELLER.address}</div>
                      <div className="rcpt-seller-line">
                        CIN: {SELLER.cin} &nbsp;|&nbsp; MSME: {SELLER.msme} &nbsp;|&nbsp; GSTIN: {SELLER.gstin}
                      </div>
                      <div className="rcpt-seller-line">
                        {SELLER.phone} &nbsp;|&nbsp; {SELLER.email} &nbsp;|&nbsp; {SELLER.website}
                      </div>
                    </div>
                    <img className="rcpt-logo rcpt-logo--right" src={assetUrl(LOGO_PATHS.ddag)} alt="Dhawan Academy" />
                  </div>

                  <hr className="rcpt-rule" />

                  <div className="rcpt-parties">
                    <div>
                      <div className="rcpt-label">Bill To</div>
                      <div><strong>{buyerName}</strong></div>
                      {buyerLines.filter(Boolean).map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                    <dl className="rcpt-meta">
                      {meta.map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt>{k}</dt>
                          <dd>{v}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>

                  <table className="rcpt-table">
                    <thead>
                      <tr>
                        <th>Description of Service</th>
                        <th>SAC</th>
                        <th className="num">Qty</th>
                        <th className="num">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((ln, idx) => (
                        <tr key={idx}>
                          <td>
                            <div>{ln.description}</div>
                            {ln.sub && <div className="rcpt-muted">{ln.sub}</div>}
                          </td>
                          <td>{SAC_CODE}</td>
                          <td className="num">{ln.quantity}</td>
                          <td className="num">₹{fmt(ln.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {charged ? (
                    <div className="rcpt-totals">
                      {taxRows.map(([k, v]) => (
                        <div key={k}>
                          <span>{k}</span>
                          <span>₹{fmt(v)}</span>
                        </div>
                      ))}
                      <div className="rcpt-grand">
                        <span>Total (incl. GST)</span>
                        <span>₹{fmt(total)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="rcpt-nocharge">{noChargeNote}</div>
                  )}

                  <div className="rcpt-foot">{footNote}</div>
                  <div className="rcpt-sign">For {SELLER.legalName}</div>
                </div>
              </div>
            </div>
          ) : (
            /* ── Interactive Custom Invoice Form ── */
            <form className="inv-custom-form" onSubmit={handleApplyCustomInvoice}>
              <div className="inv-custom-form-header">
                <div>
                  <h3 className="inv-custom-heading">Customize Receipt & Payment Data</h3>
                  <p className="inv-custom-sub">Update line items, pricing, payment details, or tax rate for {user.name || user.email}</p>
                </div>
              </div>

              {/* Invoice Meta Grid */}
              <div className="inv-form-grid">
                <div className="inv-form-field">
                  <label>Receipt No. (optional)</label>
                  <input
                    className="inv-custom-input"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </div>
                <div className="inv-form-field">
                  <label>Receipt Date</label>
                  <input
                    type="date"
                    className="inv-custom-input"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    required
                  />
                </div>
                <div className="inv-form-field">
                  <label>Payment Status</label>
                  <select
                    className="inv-custom-input"
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                  >
                    <option value="PAID">PAID IN FULL</option>
                    <option value="TRIAL">FREE TRIAL - NO CHARGE</option>
                    <option value="FREE">FREE ACCESS - NO CHARGE</option>
                    <option value="PENDING">PAYMENT PENDING</option>
                  </select>
                </div>
              </div>

              {/* Payment Details Fields */}
              <div className="inv-form-grid">
                <div className="inv-form-field">
                  <label>Payment Method</label>
                  <input
                    className="inv-custom-input"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    placeholder="e.g. Credit / Debit Card (Razorpay Secure)"
                  />
                </div>
                <div className="inv-form-field">
                  <label>Card / Bank Details</label>
                  <input
                    className="inv-custom-input"
                    value={bankOrCard}
                    onChange={(e) => setBankOrCard(e.target.value)}
                    placeholder="e.g. Razorpay / UPI"
                  />
                </div>
                <div className="inv-form-field">
                  <label>Transaction / Ref ID</label>
                  <input
                    className="inv-custom-input"
                    value={transactionRef}
                    onChange={(e) => setTransactionRef(e.target.value)}
                  />
                </div>
              </div>

              <div className="inv-divider" style={{ margin: '16px 0' }} />

              {/* Line Items Builder */}
              <div className="inv-custom-items-header">
                <h4>Line Items & Pricing</h4>
                <button
                  type="button"
                  className="inv-add-row-btn"
                  onClick={handleAddItem}
                >
                  <i className="fa fa-plus" /> Add Item Row
                </button>
              </div>

              <div className="inv-custom-items">
                {items.map((it, idx) => (
                  <div className="inv-custom-row" key={idx}>
                    <div style={{ flex: 3 }}>
                      <input
                        className="inv-custom-input"
                        placeholder="Item / License Description"
                        value={it.description}
                        onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        required
                      />
                    </div>
                    <div style={{ width: '90px' }}>
                      <input
                        className="inv-custom-input"
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={it.quantity}
                        onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                        required
                      />
                    </div>
                    <div style={{ width: '130px' }}>
                      <input
                        className="inv-custom-input"
                        type="number"
                        min="0"
                        placeholder="Price (₹)"
                        value={it.unitPrice}
                        onChange={(e) => handleItemChange(idx, 'unitPrice', e.target.value)}
                        required
                      />
                    </div>
                    <div style={{ width: '130px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: '13px' }}>
                      ₹{fmt((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0))}
                    </div>
                    <button
                      type="button"
                      className="inv-remove-row-btn"
                      onClick={() => handleRemoveItem(idx)}
                      disabled={items.length <= 1}
                      title="Remove row"
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Tax rate selector */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', alignItems: 'center', marginTop: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12.5px', fontWeight: 600 }}>GST / Tax Rate:</label>
                  <select
                    className="inv-custom-input"
                    style={{ width: '100px' }}
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(e.target.value)}
                  >
                    <option value="18">18% GST</option>
                    <option value="12">12% GST</option>
                    <option value="5">5% GST</option>
                    <option value="0">0% (Nil)</option>
                  </select>
                </div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#0284c7' }}>
                  Total: ₹{fmt(grandTotal)}
                </div>
              </div>

              {/* Form Action Buttons */}
              <div className="inv-custom-actions">
                <button type="button" className="inv-tab-btn" onClick={() => setTab('receipt')}>
                  Back to Receipt
                </button>
                <button type="submit" className="inv-action-btn inv-action-btn--pdf">
                  <i className="fa fa-check" /> Apply & View Invoice Receipt
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
